import React, { useState, useEffect, useCallback } from "react";
import { Card, CardBody, Button, Badge } from "reactstrap";
import LedgerTypeService from "../../../services/AccountingServices/LedgerTypeService";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import useTableSearch from "../../../hooks/useTableSearch";
import LedgerTypeForm from "./LedgerTypeForm";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";

const LedgerTypes = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await LedgerTypeService.getList();
      console.log("🚀 ~ LedgerTypes ~ response:", response)
      // Handle array or object response
      setData(Array.isArray(response) ? response : response?.items || []);
    } catch (error) {
      toast.error("Failed to fetch ledger types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const { searchTerm, setSearchTerm, filteredData } = useTableSearch(
    data,
    ["name", "category", "description"]
  );

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const toggleForm = () => {
    if (isFormOpen) setSelectedItem(null);
    setIsFormOpen(!isFormOpen);
  };

  const toggleDelete = () => setIsDeleteOpen(!isDeleteOpen);

  const handleAdd = () => {
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await LedgerTypeService.delete(selectedItem.id);
      toast.success("Ledger Type deleted successfully");
      fetchData();
      toggleDelete();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete ledger type");
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "80px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => <span className="fw-semibold text-primary">{row.name}</span>,
    },
    {
      name: "Category",
      selector: (row) => row.category,
      sortable: true,
      cell: (row) => {
        const colors = {
          ASSETS: "success",
          LIABILITIES: "danger",
          INCOME: "info",
          EXPENSES: "warning",
          EQUITY: "primary",
        };
        return (
          <Badge color={colors[row.category] || "secondary"} pill className="text-uppercase px-3 py-2">
            {row.category}
          </Badge>
        );
      },
    },
    {
      name: "Description",
      selector: (row) => row.description || "No description",
      sortable: true,
      wrap: true,
    },
  ];

  const stats = [
    {
      title: "Total Types",
      value: data.length,
      color: "#3B82F6",
      icon: "bx bx-list-ul",
    },
    {
      title: "Assets Types",
      value: data.filter((t) => t.category === "ASSETS").length,
      color: "#10B981",
      icon: "bx bx-trending-up",
    },
    {
      title: "Expenses Types",
      value: data.filter((t) => t.category === "EXPENSES").length,
      color: "#F59E0B",
      icon: "bx bx-trending-down",
    },
  ];

  return (
    <div className="page-content p-0">
      <ResponsiveDataManagement
        title="Ledger Types"
        pageTitle="Accounting Setup"
        mobileCardTitle="Ledger Types List"
        data={data} // Pass full data to let component handle internal searching if desired, or pass filteredData
        columns={columns}
        stats={stats}
        searchKeys={["name", "category", "description"]}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        loading={loading}
        onSearch={setSearchTerm}
        searchTerm={searchTerm}
        emptyStateMessage="No ledger types found"
      />

      <LedgerTypeForm
        isOpen={isFormOpen}
        toggle={toggleForm}
        selectedItem={selectedItem}
        refetch={fetchData}
      />

      <DeleteModal
        show={isDeleteOpen}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDeleteConfirm}
        itemName={selectedItem?.name}
      />
    </div>
  );
};

export default LedgerTypes;
